/**
 * Inbox Table Columns
 * Gmail-inspired column definitions with entity links and clean typography.
 */

import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { CorrespondenceChannel } from '@/services/api/correspondenceService';

import { ChannelLabel } from '../header';
import { RecipientDisplay } from './components/RecipientDisplay';
import { StatusDot } from './components/StatusDot';
import { PACKAGE_TYPE_CONFIG } from './constants';
import type { PackageRowData } from './types';

// Extend TanStack Table column meta
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    className?: string;
    width?: string | number;
  }
}

const columnHelper = createColumnHelper<PackageRowData>();

interface UseInboxColumnsOptions {
  onRowClick: (id: string) => void;
}

export const useInboxColumns = ({ onRowClick }: UseInboxColumnsOptions) =>
  useMemo(
    () => [
      // Selection
      columnHelper.display({
        id: 'select',
        meta: { width: 48, className: 'flex-shrink-0 pl-6' },
        header: () => null,
        cell: ({ row }) => (
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            className={cn(
              'transition-opacity',
              !row.getCanSelect() && 'opacity-30',
            )}
          >
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(v) => row.toggleSelected(!!v)}
              disabled={!row.getCanSelect()}
              className="data-[state=checked]:bg-stone-900 data-[state=checked]:border-stone-900"
            />
          </div>
        ),
      }),

      // Status indicator (dot only)
      columnHelper.display({
        id: 'status',
        meta: { width: 36, className: 'flex-shrink-0 justify-center' },
        cell: ({ row }) => (
          <StatusDot
            renderStatus={row.original.status}
            transmissionStatus={row.original.message?.transmission_status}
          />
        ),
      }),

      // Package type label
      columnHelper.accessor('package_type', {
        header: 'Type',
        meta: { width: 80, className: 'flex-shrink-0' },
        enableSorting: true,
        cell: ({ row }) => {
          const config = PACKAGE_TYPE_CONFIG[row.original.package_type];
          return (
            <span
              className={cn(
                'text-[11px] font-bold uppercase tracking-wider',
                row.original.is_transmitted
                  ? 'text-stone-400'
                  : (config?.color ?? 'text-stone-500'),
              )}
            >
              {config?.shortLabel ?? 'Custom'}
            </span>
          );
        },
      }),

      // Recipient (main column - clickable)
      columnHelper.accessor('recipient_display_name', {
        header: 'Recipient',
        meta: { className: 'flex-1 min-w-0 px-4' },
        enableSorting: true,
        cell: ({ row }) => {
          const pkg = row.original;
          const entityId = pkg.recipient_id;

          return (
            <div
              role="button"
              tabIndex={0}
              onClick={() => onRowClick(pkg.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRowClick(pkg.id);
                }
              }}
              className="flex-1 min-w-0 cursor-pointer"
            >
              <RecipientDisplay
                displayName={pkg.recipient_display_name}
                entityType={pkg.recipient_entity_type}
                entityId={entityId}
                isRead={pkg.is_transmitted}
              />
            </div>
          );
        },
      }),

      // Transmission method
      columnHelper.display({
        id: 'transmission',
        header: 'Via',
        meta: { width: 80, className: 'flex-shrink-0 hidden lg:flex' },
        cell: ({ row }) => {
          const pkg = row.original;
          const transmissionType = pkg.transmission?.type as
            | CorrespondenceChannel
            | undefined;

          if (!transmissionType) {
            return <span className="text-xs text-stone-400">—</span>;
          }

          return (
            <ChannelLabel
              channel={transmissionType}
              className="text-xs text-stone-600"
            />
          );
        },
      }),

      // Status label (text)
      columnHelper.display({
        id: 'statusLabel',
        header: 'Status',
        meta: {
          width: 105,
          className: 'flex-shrink-0 hidden md:flex justify-end',
        },
        cell: ({ row }) => {
          const txStatus = row.original.message?.transmission_status;
          const renderStatus = row.original.status;

          const label = txStatus
            ? TX_STATUS_LABELS[txStatus]
            : RENDER_STATUS_LABELS[renderStatus];

          const isInProgress =
            txStatus === 'pending' ||
            txStatus === 'sending' ||
            renderStatus === 'queued_for_render' ||
            renderStatus === 'generating';

          return (
            <span
              className={cn(
                'text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap',
                isInProgress && 'bg-blue-50 text-blue-700',
                txStatus === 'failed' && 'bg-red-50 text-red-700',
                txStatus === 'sent' && 'bg-stone-100 text-stone-600',
                txStatus === 'delivered' && 'bg-emerald-50 text-emerald-700',
                !txStatus &&
                  renderStatus === 'ready' &&
                  'bg-emerald-50 text-emerald-700',
                !txStatus &&
                  renderStatus === 'draft' &&
                  'bg-stone-100 text-stone-500',
                !txStatus &&
                  renderStatus === 'missing_fields' &&
                  'bg-amber-50 text-amber-700',
                !txStatus &&
                  renderStatus === 'error' &&
                  'bg-red-50 text-red-700',
              )}
            >
              {label}
            </span>
          );
        },
      }),

      // Date
      columnHelper.accessor('displayDate', {
        header: 'Date',
        meta: { width: 120, className: 'flex-shrink-0 justify-end pr-6' },
        enableSorting: true,
        cell: ({ row }) => (
          <span
            className={cn(
              'text-xs tabular-nums',
              !row.original.is_transmitted
                ? 'text-stone-900 font-medium'
                : 'text-stone-500',
            )}
          >
            {row.original.displayDate}
          </span>
        ),
      }),
    ],
    [onRowClick],
  );

// Status label mappings
const TX_STATUS_LABELS: Record<string, string> = {
  pending: 'Queued',
  sending: 'Sending',
  sent: 'Sent',
  delivered: 'Delivered',
  failed: 'Failed',
  bounced: 'Bounced',
};

const RENDER_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  queued_for_render: 'Queued',
  generating: 'Generating',
  ready: 'Ready',
  missing_fields: 'Incomplete',
  error: 'Error',
};
